import { WarningOutlined } from '@ant-design/icons';
import { Flex, Popover, Typography } from 'antd';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { defaultIconTaskType, getIconByPriorityTask, getTaskTypeIcon } from '../../calendar/calendarFunctions';
import CalendarItemMonth from '../../calendar/calendarItemMonth';
import useAccessForTask from '../../hooks/useAccessForTask';
import { RootState } from '../../store';
import { setterCalendar } from '../../store/slices/calendarSlice';
import { setterTaskBoardDraggable } from '../../store/slices/taskBoardDraggableSlice';
import { ItemType, MappingItemIdIndexType } from '../../types';
import { TaskBoardTaskType } from '../taskBoardTypes';
import { TaskBoardTaskAssignee } from './taskBoardTaskAssignee';
import { TaskBoardTaskItemStatus } from './taskBoardTaskItemStatus';

interface TaskItemProps {
    task: TaskBoardTaskType;
}

const GHOST_WIDTH = '270px';

const { Title, Text } = Typography;

export const TaskBoardTaskItem: React.FC<TaskItemProps> = ({ task }) => {
    const dispatch = useDispatch();

    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);
    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        // Clone the element to use as the drag image
        const clone = e.currentTarget.cloneNode(true) as HTMLElement;

        // Position the clone off-screen so it does not show in the document flow
        clone.style.position = 'absolute';
        clone.style.top = '-1000px';
        clone.style.left = '-1000px';
        clone.style.display = 'block';
        clone.style.width = GHOST_WIDTH;

        document.body.appendChild(clone);
        // Set the custom drag image to the clone
        // Adjust the offset (here 0,0) as needed
        e.dataTransfer.setDragImage(clone, 0, 0);
        // Remove the clone after a brief delay so that the browser has time to capture it
        setTimeout(() => {
            document.body.removeChild(clone);
            dispatch(
                setterTaskBoardDraggable({
                    draggingTask: task,
                }),
            );
        }, 0);
    };

    const isAccessStatus = useAccessForTask(task.dataFromServer, [
        'schedule-task.all.edit',
        'schedule-task.all.manage',
        'schedule-task.owner.edit',
        'schedule-task.owner.manage',
        'schedule-task.assignee.edit',
        'schedule-task.assignee.manage',
    ]);

    const scheduleItem = task.scheduleItemId ? (itemsList[mappingItemIdIndex[task.scheduleItemId]] ?? null) : null;

    return (
        <div
            draggable={isAccessStatus}
            onDragStart={handleDragStart}
            onDragEnd={() => {
                dispatch(
                    setterTaskBoardDraggable({
                        draggingTask: null,
                    }),
                );
            }}
            onClick={() => {
                dispatch(
                    setterCalendar({
                        currentTask: task.dataFromServer,
                    }),
                );
            }}
            style={{
                marginBottom: 8,
                border: '2px solid #cfdff5',
                padding: '10px 10px 10px 0',
                borderRadius: 8,
                background: 'white',
                cursor: isAccessStatus ? 'grab' : 'auto',
                position: 'relative',
            }}
        >
            <Flex justify={'space-between'}>
                {!isAccessStatus && (
                    <Popover content="Not enough permissions to edit">
                        <WarningOutlined style={{ color: '#FFA500', position: 'absolute', right: '5px', top: '5px' }} />
                    </Popover>
                )}

                <Flex align="center" style={{ width: 40 }} justify={'center'}>
                    {task.type ? getTaskTypeIcon(task.type.toString(), 16) : defaultIconTaskType()}
                </Flex>

                <Flex vertical style={{ width: 'calc(100% - 40px)' }}>
                    <Flex vertical>
                        <Title level={4} style={{ marginBottom: 0, marginBlockStart: 0 }}>
                            {task.name}
                        </Title>
                        <Flex align={'center'} gap={4}>
                            {(task.projectName || task.scheduleItemName) && (
                                <>
                                    <Text type="secondary">{task.projectName}</Text>
                                    <Text type="secondary">
                                        {task.projectName && task.scheduleItemName ? '→' : null}
                                    </Text>
                                    <Flex>
                                        <CalendarItemMonth
                                            item={scheduleItem}
                                            day={1}
                                            staticSize
                                            isInteractive
                                            forceCaption
                                            currentDate={scheduleItem?.startDate!}
                                        />
                                    </Flex>
                                </>
                            )}
                        </Flex>
                    </Flex>

                    <Flex justify="space-between">
                        <Flex vertical style={{ width: 'calc(100% - 60px)' }}>
                            <Text strong>{task.startDatetime}</Text>
                            <Flex gap={15} align="center" justify={'space-between'} style={{ overflow: 'hidden' }}>
                                {task.status && <TaskBoardTaskItemStatus status={task.status} />}
                                <Text type="secondary" style={{ textWrap: 'nowrap', marginRight: '10%' }}>
                                    {task.subtaskProgress}
                                </Text>
                            </Flex>
                        </Flex>
                        <Flex justify={'right'} align={'end'} style={{ width: 60 }}>
                            <Flex style={{ paddingRight: 10, paddingBottom: 10 }}>
                                {task.priority && getIconByPriorityTask(task.priority)}
                            </Flex>
                            {task.assignee && <TaskBoardTaskAssignee userId={task.assignee} />}
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>
        </div>
    );
};
